import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, OnInit, Output, input, signal, effect, Renderer2, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigBirthDate } from '../../interfaces/interfaces';

@Component({
  selector: 'app-birthdate',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, CommonModule],
  templateUrl: './birthdate.component.html',
  styleUrl: './birthdate.component.css'
})
export class BirthdateComponent implements OnInit, AfterViewInit {

  config = input<ConfigBirthDate>();

  @Output() submit: EventEmitter<any> = new EventEmitter<any>();

  form: FormGroup;

  months = [
    { value: 1, name: 'Enero' }, { value: 2, name: 'Febrero' },
    { value: 3, name: 'Marzo' }, { value: 4, name: 'Abril' },
    { value: 5, name: 'Mayo' }, { value: 6, name: 'Junio' },
    { value: 7, name: 'Julio' }, { value: 8, name: 'Agosto' },
    { value: 9, name: 'Septiembre' }, { value: 10, name: 'Octubre' },
    { value: 11, name: 'Noviembre' }, { value: 12, name: 'Diciembre' },
  ];

  allowedYears: number[] = [];
  daysInMonth: number[] = [];

  offTimeOut = signal(false);
  hasWritten = signal(false);
  private errorDebounceTimer: any;
  private countdownTimer: any;
  private viewInitialized = signal(false);

  @ViewChild('formContainer') formContainer!: ElementRef;
  @ViewChild('labelElement') labelElement!: ElementRef; // Referencia al label

  constructor(private renderer: Renderer2){
    this.form = new FormGroup({
      year: new FormControl('', Validators.required),
      month: new FormControl('', Validators.required),
      day: new FormControl('', Validators.required),
    });

    this.generateAllowedYears();
    this.listenToFormChanges();

    effect(() => {
      const cfg = this.config();
      if (cfg?.defaultValue) {
        this.form.patchValue({
          month: cfg.defaultValue.month,
          year: cfg.defaultValue.year
        });
        this.onMonthOrYearChange();
        this.form.patchValue({
          day: cfg.defaultValue.day
        });
        this.clearCountdown();
      } else {
        setTimeout(() => {
          if (!this.form.valid) {
            this.startCountdown();
          }
        }, 100);
      }
    });

    this.form.valueChanges.subscribe(() => {
      this.hasWritten.set(true);
      this.resetErrorDebounce();
      
      if (this.form.valid) {
        this.clearCountdown();
        this.offTimeOut.set(false);
        this.getStyleEnabled();
      }
    });
  }

  ngOnInit() {
    this.onMonthOrYearChange();
  }

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    this.applyInitialStyles();
  }

  private applyInitialStyles() {
    this.getStyleEnabled();
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized()) {
      callback();
    }
  }

  listenToFormChanges() {
    this.form.valueChanges.subscribe(values => {
      if (this.form.valid) {
        this.submit.emit(values);
      }
    });
  }

  generateAllowedYears(){
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear - 18;
    for(let year = maxYear; year >= 1900; year--){
      this.allowedYears.push(year);
    }
  }

  onMonthOrYearChange() {
    const month = this.form.get('month')?.value;
    const year = this.form.get('year')?.value;

    if(month && year){
      const days = new Date(year, month, 0).getDate();
      this.daysInMonth = Array.from({length: days}, (_, i) => i + 1);

      const currentDay = this.form.get('day')?.value;
      if(currentDay > days){
        this.form.get('day')?.setValue('');
      }
    } else {
      this.daysInMonth = [];
    }
  }

  onSubmit(){
    if(this.form.valid){
      this.submit.emit(this.form.value);
    }
  }

  startCountdown() {
    this.clearCountdown();
    this.countdownTimer = setTimeout(() => {
      if (!this.form.valid && !this.hasWritten()) {
        this.offTimeOut.set(true);
        this.getStyleError();
      }
    }, 8000);
  }

  clearCountdown() {
    if (this.countdownTimer) {
      clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  resetErrorDebounce() {
    if (this.errorDebounceTimer) {
      clearTimeout(this.errorDebounceTimer);
    }

    this.errorDebounceTimer = setTimeout(() => {
      if (this.form.invalid && (this.form.touched || this.form.dirty) || this.offTimeOut()) {
        this.getStyleError();
      } else if (this.form.valid) {
        this.getStyleEnabled();
      }
    }, 1700);
  }

  getStyleError(){
    this.safeApplyStyles(() => {
      // Aplicar estilo de error al label
      if (this.labelElement?.nativeElement) {
        this.renderer.removeClass(this.labelElement.nativeElement, 'text-label');
        this.renderer.removeClass(this.labelElement.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.labelElement.nativeElement, 'text-label-error');
      }

      // Aplicar estilo de error al contenedor de selects
      if (this.formContainer?.nativeElement) {
        const selectsContainer = this.formContainer.nativeElement.querySelector('div.flex');
        if (selectsContainer) {
          this.renderer.removeClass(selectsContainer, 'border-blue-800');
          this.renderer.addClass(selectsContainer, 'border-red-500');
        }
        
        // Aplicar estilo a los selects individuales
        const selects = this.formContainer.nativeElement.querySelectorAll('select');
        selects.forEach((select: HTMLElement) => {
          this.renderer.removeClass(select, 'border-blue-800');
          this.renderer.addClass(select, 'border-red-500');
        });
      }
    });
  }
  
  getStyleEnabled(){
    this.safeApplyStyles(() => {
      // Restaurar estilo normal al label
      if (this.labelElement?.nativeElement) {
        this.renderer.removeClass(this.labelElement.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.labelElement.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.labelElement.nativeElement, 'text-label');
      }

      // Restaurar estilo normal al contenedor de selects
      if (this.formContainer?.nativeElement) {
        const selectsContainer = this.formContainer.nativeElement.querySelector('div.flex');
        if (selectsContainer) {
          this.renderer.removeClass(selectsContainer, 'border-red-500');
          this.renderer.addClass(selectsContainer, 'border-blue-800');
        }
        
        // Restaurar estilos a los selects individuales
        const selects = this.formContainer.nativeElement.querySelectorAll('select');
        selects.forEach((select: HTMLElement) => {
          this.renderer.removeClass(select, 'border-red-500');
          this.renderer.addClass(select, 'border-blue-800');
        });
      }
    });
  }

  getStyleTextDisabled(){
    this.safeApplyStyles(() => {
      if (this.labelElement?.nativeElement) {
        this.renderer.removeClass(this.labelElement.nativeElement, 'text-label');
        this.renderer.removeClass(this.labelElement.nativeElement, 'text-label-error');
        this.renderer.addClass(this.labelElement.nativeElement, 'text-label-disabled');
      }
    });
  }

  ngOnDestroy() {
    if (this.errorDebounceTimer) {
      clearTimeout(this.errorDebounceTimer);
    }
    this.clearCountdown();
  }
}